import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCoreRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import type { PreisToolItem } from '@/api/preis-tool/preis-tool.types';
import type { PriceField } from '../helpers';
import { ROW_STATE_CLASS, rowColourState, type RowState } from '../row-states';
import { DsTable } from '../../shared/ds-table.component';
import {
  hitListColumns,
  EDITABLE_COLS,
  type HitListTableMeta,
  type PriceDrafts,
  type ActiveCell,
} from './hit-list-columns';
import type { CellNavDirection } from './cells/editable-price-cell';

interface HitListProps {
  rows: PreisToolItem[];
  total: number;
  selectedId: string | undefined;
  setSelectedId: (id: string) => void;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  isPending: boolean;
  drafts: PriceDrafts;
  onPriceCommit: (itemId: string, field: PriceField, value: number | null) => void;
  activeCell: ActiveCell | null;
  onActiveCellChange: (cell: ActiveCell | null) => void;
  /** Legend states per item id, highest priority first (`row-states.ts`). */
  rowStates: ReadonlyMap<string, readonly RowState[]>;
  /** Active legend filters; they decide the row colour while set. */
  legendStates: readonly RowState[];
  /** "Niedrige Marge" limit for the margin pills of the price cells. */
  lowMarginPct: number;
  /** Whether the current user may edit prices (`ITEMS_EDIT_PRICES`). */
  canEdit: boolean;
}

export function HitList({ rows, selectedId, setSelectedId, sorting, onSortingChange, isPending, drafts, onPriceCommit, activeCell, onActiveCellChange, rowStates, legendStates, lowMarginPct, canEdit }: HitListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const setActiveCell = onActiveCellChange;
  const [editingCell, setEditingCell] = useState<ActiveCell | null>(null);
  const [initialKey, setInitialKey] = useState<string | null>(null);

  const refocus = useCallback(() => {
    setTimeout(() => containerRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    if (activeCell && !editingCell) refocus();
  }, [activeCell, editingCell, refocus]);

  const navigate = useCallback((dir: CellNavDirection) => {
    if (dir === 'cancel') {
      setEditingCell(null);
      return;
    }

    if (!activeCell) return;
    const colIdx = EDITABLE_COLS.indexOf(activeCell.colId);
    let newRow = activeCell.rowIndex;
    let newColIdx = colIdx;

    if (dir === 'down') {
      newRow = Math.min(activeCell.rowIndex + 1, rows.length - 1);
    } else if (dir === 'up') {
      newRow = Math.max(activeCell.rowIndex - 1, 0);
    } else if (dir === 'right') {
      if (colIdx < EDITABLE_COLS.length - 1) {
        newColIdx = colIdx + 1;
      } else {
        newColIdx = 0;
        newRow = Math.min(activeCell.rowIndex + 1, rows.length - 1);
      }
    } else if (dir === 'left') {
      if (colIdx > 0) {
        newColIdx = colIdx - 1;
      } else {
        newColIdx = EDITABLE_COLS.length - 1;
        newRow = Math.max(activeCell.rowIndex - 1, 0);
      }
    }

    const next = { rowIndex: newRow, colId: EDITABLE_COLS[newColIdx] };
    setActiveCell(next);
    setEditingCell(next);
  }, [activeCell, rows.length, setActiveCell]);

  const onCellNav = useCallback((dir: CellNavDirection) => {
    setInitialKey(null);
    navigate(dir);
    if (dir === 'cancel') refocus();
  }, [navigate, refocus]);

  const meta = useMemo<HitListTableMeta>(
    () => ({ drafts, onPriceCommit, activeCell, editingCell, onCellNav, initialKey, rowStates, lowMarginPct, canEdit }),
    [drafts, onPriceCommit, activeCell, editingCell, onCellNav, initialKey, rowStates, lowMarginPct, canEdit],
  );

  const table = useReactTable({
    data: rows,
    columns: hitListColumns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      onSortingChange(next);
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    getRowId: (row) => row.id,
    enableSortingRemoval: false,
    meta,
  });

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (editingCell) return;

    const isEditable =
      canEdit && activeCell && EDITABLE_COLS.includes(activeCell.colId);

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      if (!activeCell) {
        setActiveCell({ rowIndex: 0, colId: EDITABLE_COLS[0] });
        return;
      }
      const colIdx = EDITABLE_COLS.indexOf(activeCell.colId);
      let newRow = activeCell.rowIndex;
      let newColIdx = Math.max(0, colIdx);

      if (e.key === 'ArrowDown') newRow = Math.min(newRow + 1, rows.length - 1);
      if (e.key === 'ArrowUp') newRow = Math.max(newRow - 1, 0);
      if (e.key === 'ArrowRight') newColIdx = Math.min(newColIdx + 1, EDITABLE_COLS.length - 1);
      if (e.key === 'ArrowLeft') newColIdx = Math.max(newColIdx - 1, 0);

      setActiveCell({ rowIndex: newRow, colId: EDITABLE_COLS[newColIdx] });
    }

    if (e.key === 'Enter' && isEditable) {
      e.preventDefault();
      setInitialKey(null);
      setEditingCell(activeCell);
    }

    if (isEditable && e.key.length === 1 && /[0-9.,-]/.test(e.key)) {
      e.preventDefault();
      setEditingCell(activeCell);
      setInitialKey(e.key);
    }

    if (e.key === 'Tab' && activeCell) {
      e.preventDefault();
      const colIdx = EDITABLE_COLS.indexOf(activeCell.colId);
      let newRow = activeCell.rowIndex;
      let newColIdx = Math.max(0, colIdx);

      if (e.shiftKey) {
        if (newColIdx > 0) newColIdx--;
        else { newColIdx = EDITABLE_COLS.length - 1; newRow = Math.max(newRow - 1, 0); }
      } else {
        if (newColIdx < EDITABLE_COLS.length - 1) newColIdx++;
        else { newColIdx = 0; newRow = Math.min(newRow + 1, rows.length - 1); }
      }
      setActiveCell({ rowIndex: newRow, colId: EDITABLE_COLS[newColIdx] });
    }

    if (e.key === 'Escape') {
      setActiveCell(null);
      setEditingCell(null);
      refocus();
    }
  }, [activeCell, editingCell, rows.length, canEdit]);

  const rowClassName = (a: PreisToolItem) => {
    const classes: string[] = [];
    // The colour of the legend state with the highest priority (among the active filters).
    const primary = rowColourState(rowStates.get(a.id), legendStates);
    if (primary) classes.push('pt-state', ROW_STATE_CLASS[primary]);
    if (drafts[a.id] && Object.keys(drafts[a.id]).length > 0) classes.push('ds-row-dirty');
    return classes.join(' ');
  };

  const cellClassName = useCallback((rowIndex: number, colId: string) => {
    const classes: string[] = [];
    if (activeCell?.rowIndex === rowIndex && activeCell?.colId === colId) classes.push('ds-cell-active');
    if (EDITABLE_COLS.includes(colId)) {
      const item = rows[rowIndex];
      if (item && drafts[item.id]?.[colId as PriceField] !== undefined) classes.push('ds-cell-dirty');
    }
    return classes.length ? classes.join(' ') : undefined;
  }, [activeCell, rows, drafts]);

  const onCellClick = useCallback((row: PreisToolItem, rowIndex: number, colId: string) => {
    setSelectedId(row.id);
    if (EDITABLE_COLS.includes(colId)) {
      setActiveCell({ rowIndex, colId });
      setEditingCell(null);
      refocus();
    }
  }, [refocus, setSelectedId]);

  return (
    <div ref={containerRef} className="pt-hitlist" onKeyDown={handleKeyDown} tabIndex={0} style={{ outline: 'none' }}>
      <DsTable
        table={table}
        selectedRowId={selectedId}
        onRowClick={(a) => setSelectedId(a.id)}
        rowClassName={rowClassName}
        cellClassName={cellClassName}
        onCellClick={onCellClick}
        emptyMessage={isPending ? undefined : 'Keine Treffer für diese Filter.'}
        fixedLayout
        scrollToIndex={activeCell?.rowIndex}
      />
    </div>
  );
}

export type { SortingState };
